import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthContextType, AuthState, User } from '../types';
import { getToken, setToken, removeToken, getUserFromToken, isTokenExpired } from '../utils/auth';

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

// Types for reducer actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN'; payload: { token: string; user: User } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'INITIALIZE_AUTH'; payload: { token: string; user: User } | null };

// Reducer to handle authentication state
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'LOGIN':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'INITIALIZE_AUTH':
      if (action.payload) {
        return {
          ...state,
          user: action.payload.user,
          token: action.payload.token,
          isAuthenticated: true,
          isLoading: false,
        };
      }
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props for provider
interface AuthProviderProps {
  children: ReactNode;
}

// Authentication context provider
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Function to initialize authentication when app loads
  const initializeAuth = () => {
    const token = getToken();
    
    if (token && !isTokenExpired()) {
      const user = getUserFromToken();
      if (user) {
        dispatch({ 
          type: 'INITIALIZE_AUTH', 
          payload: { token, user } 
        });
        return;
      }
    }
    
    // If no valid token, clean state
    if (token) {
      removeToken(); // Clean invalid token
    }
    
    dispatch({ type: 'INITIALIZE_AUTH', payload: null });
  };

  // Initialize authentication when component mounts
  useEffect(() => {
    initializeAuth();
  }, []);

  // Login function
  const login = (token: string) => {
    try {
      setToken(token);
      const user = getUserFromToken();
      
      if (!user) {
        throw new Error('Token inválido: no se pudo obtener información del usuario');
      }

      dispatch({ 
        type: 'LOGIN', 
        payload: { token, user } 
      });
      
      // Navigate to appropriate dashboard based on role
      const navigateToDashboard = () => {
        switch (user.role) {
          case 'admin':
            navigate('/buscador');
            break;
          case 'fiscalizacion_editor':
          case 'fiscalizacion_lector':
            navigate('/buscador');
            break;
          case 'observatorio_editor':
            navigate('/estadisticas');
            break;
          case 'observatorio_lector':
          case 'observatorio_observador':
            navigate('/muestras/completar');
            break;
          case 'informes_lector':
            navigate('/buscador');
            break;
          default:
            navigate('/buscador');
        }
      };
      
      navigateToDashboard();
    } catch (error) {
      console.error('Error en login:', error);
      removeToken();
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    removeToken();
    dispatch({ type: 'LOGOUT' });
    navigate('/auth');
  };

  // Function to update user information
  const updateUser = (user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}; 